import { readContract } from '@wagmi/core'
import Big from 'big.js'
import type { CurrencyMetadata } from 'indices-list'
import { atom } from 'jotai'
import { networkToChain, wagmiConfig } from 'services/wallet/chains'
import { formatWeiToAmount } from 'utils'
import { type Address, erc20Abi, isAddressEqual, maxUint256, zeroAddress } from 'viem'
import { createAtomsWithQuery } from '../atomic-factory'
import { QUERY_KEY_USER_ALLOWANCE } from '../keys'
import { DEFAULT_REFETCH_INTERVAL } from '../refetch-intervals'
import { defaultCurrency } from '../zero-ex-requests/quotes/model'
import type { AllowanceRequestAtoms } from './model'
import { z } from 'zod'

const userAllowanceQuery = async <T>({
  queryKey: [, address, currency, spender],
}: {
  queryKey: [string, Address, CurrencyMetadata, Address]
}) => {
  if (isAddressEqual(address, zeroAddress)) return { value: Big(0), taker: spender } as T
  if (isAddressEqual(currency.address, zeroAddress))
    return {
      value: formatWeiToAmount(maxUint256.toString(), currency.decimals),
      taker: spender,
    } as T
  const allowance = await readContract(wagmiConfig, {
    address: currency.address,
    abi: erc20Abi,
    chainId: networkToChain(currency.chainId).id,
    functionName: 'allowance',
    args: [address, spender],
  })
  const result = z.bigint().parse(allowance)

  return { value: formatWeiToAmount(result, currency.decimals), taker: spender } as T
}

export const userAllowanceQueryAtomsFactory = () => {
  const userAddressAtom = atom<Address>(zeroAddress)
  const currentAssetAtom = atom<CurrencyMetadata>(defaultCurrency)
  const takerAddressAtom = atom<Address>(zeroAddress)

  const mainProperties = createAtomsWithQuery<
    { taker: Address; value: Big },
    Address,
    CurrencyMetadata,
    Address,
    [string, Address, CurrencyMetadata, Address]
  >(
    { first: userAddressAtom, second: currentAssetAtom, third: takerAddressAtom },
    QUERY_KEY_USER_ALLOWANCE,
    userAllowanceQuery<{ taker: Address; value: Big }>,
    DEFAULT_REFETCH_INTERVAL,
    'userAllowanceQuery',
  )

  return {
    userAddress: userAddressAtom,
    currentAsset: currentAssetAtom,
    takerAddress: takerAddressAtom,
    ...mainProperties,
  } as AllowanceRequestAtoms
}
